import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Grid,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { supabase } from '../services/supabaseClient';
import { useTheme, useMediaQuery } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Profile: React.FC = () => {
  const { user, session } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Informations du profil
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    organization: '',
    role: '',
    agent_code: '',
  });

  // Changement de mot de passe
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Statistiques
  const [stats, setStats] = useState({
    projects: 0,
    tasks: 0,
    reports: 0,
    messages: 0,
  });

  // Activités récentes
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        organization: user.organization || '',
        role: user.role || '',
        agent_code: (user as any).agent_code || '',
      });
      loadUserStats();
      loadRecentActivities();
    }
  }, [user]);

  const loadUserStats = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const [projectsRes, reportsRes, messagesRes] = await Promise.all([
        api.projects.getAll().catch(() => ({ projects: [] })),
        api.reports.getAll().catch(() => ({ reports: [] })),
        api.messages.getAll().catch(() => ({ messages: [] })),
      ]);

      const userProjects = projectsRes.projects?.filter(
        (p: any) => p.created_by === user.id || p.assigned_to === user.id
      ) || [];
      
      const userReports = reportsRes.reports?.filter(
        (r: any) => r.created_by === user.id
      ) || [];

      const userMessages = messagesRes.messages?.filter(
        (m: any) => m.sender_id === user.id || m.recipient_id === user.id
      ) || [];

      // Compter les tâches des projets de l'utilisateur
      let taskCount = 0;
      for (const project of userProjects) {
        try {
          const projectDetail = await api.projects.getById(project.id);
          taskCount += projectDetail.tasks?.length || 0;
        } catch (e) {
          // Ignorer les erreurs
        }
      }

      setStats({
        projects: userProjects.length,
        tasks: taskCount,
        reports: userReports.length,
        messages: userMessages.length,
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    if (!user) return;
    try {
      const activities: any[] = [];
      
      // Récupérer les projets récents
      const projectsRes = await api.projects.getAll().catch(() => ({ projects: [] }));
      const recentProjects = (projectsRes.projects || [])
        .filter((p: any) => p.created_by === user.id)
        .slice(0, 5)
        .map((p: any) => ({
          type: 'project',
          title: `Projet créé: ${p.title}`,
          date: p.created_at,
          icon: <PersonIcon />,
        }));

      // Récupérer les rapports récents
      const reportsRes = await api.reports.getAll().catch(() => ({ reports: [] }));
      const recentReports = (reportsRes.reports || [])
        .filter((r: any) => r.created_by === user.id)
        .slice(0, 5)
        .map((r: any) => ({
          type: 'report',
          title: `Rapport soumis: ${r.title}`,
          date: r.created_at,
          icon: <HistoryIcon />,
        }));

      activities.push(...recentProjects, ...recentReports);
      activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setRecentActivities(activities.slice(0, 10));
    } catch (error) {
      console.error('Erreur lors du chargement des activités:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Mettre à jour le profil dans Supabase
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name: profileData.name,
          organization: profileData.organization || null,
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Mettre à jour l'email dans Supabase Auth si changé
      if (profileData.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: profileData.email,
        });
        if (emailError) throw emailError;
      }

      setSuccess('Profil mis à jour avec succès');
      setIsEditingProfile(false);
      
      // Recharger la page après un court délai pour voir le message de succès
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      setError(error.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }

      // Mettre à jour le mot de passe dans Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });

      if (updateError) throw updateError;

      setSuccess('Mot de passe modifié avec succès');
      setPasswordDialogOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Erreur lors du changement de mot de passe:', error);
      setError(error.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrateur',
      agence: 'Agence',
      soebra: 'SOBEBRA',
      agent_terrain: 'Agent Terrain',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      admin: 'error',
      agence: 'primary',
      soebra: 'secondary',
      agent_terrain: 'success',
    };
    return colors[role] || 'default';
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* En-tête du profil */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: 'primary.main',
              fontSize: '3rem',
            }}
          >
            {user.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h4" component="h1">
                {user.name || 'Utilisateur'}
              </Typography>
              <Chip
                label={getRoleLabel(user.role)}
                color={getRoleColor(user.role)}
                size="small"
              />
            </Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <EmailIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              {user.email}
            </Typography>
            {user.organization && (
              <Typography variant="body2" color="text.secondary">
                <BusinessIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                {user.organization}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Messages d'erreur et de succès */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Onglets */}
      <Paper>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Informations" />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Sécurité" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Activités" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Statistiques" />
        </Tabs>

        {/* Onglet Informations */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Informations personnelles</Typography>
              {!isEditingProfile ? (
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  onClick={() => setIsEditingProfile(true)}
                >
                  Modifier
                </Button>
              ) : (
                <Box display="flex" gap={1}>
                  <Button
                    startIcon={<SaveIcon />}
                    variant="contained"
                    onClick={handleSaveProfile}
                    disabled={saving}
                  >
                    Enregistrer
                  </Button>
                  <Button
                    startIcon={<CancelIcon />}
                    variant="outlined"
                    onClick={() => {
                      setIsEditingProfile(false);
                      setProfileData({
                        name: user.name || '',
                        email: user.email || '',
                        organization: user.organization || '',
                        role: user.role || '',
                        agent_code: (user as any).agent_code || '',
                      });
                    }}
                  >
                    Annuler
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom complet"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  disabled={!isEditingProfile}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  disabled={!isEditingProfile}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Organisation"
                  value={profileData.organization}
                  onChange={(e) => setProfileData({ ...profileData, organization: e.target.value })}
                  disabled={!isEditingProfile}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled>
                  <InputLabel>Rôle</InputLabel>
                  <Select
                    value={profileData.role}
                    label="Rôle"
                    startAdornment={
                      <InputAdornment position="start">
                        <SecurityIcon />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value={profileData.role}>{getRoleLabel(profileData.role)}</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Le rôle ne peut pas être modifié depuis cette interface
                </Typography>
              </Grid>
              {user.role === 'agent_terrain' && profileData.agent_code && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Code Agent"
                    value={profileData.agent_code}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SecurityIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Votre code agent unique pour identifier vos soumissions de sondages"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </TabPanel>

        {/* Onglet Sécurité */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Sécurité du compte
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Mot de passe
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Modifiez votre mot de passe pour sécuriser votre compte
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<LockIcon />}
                    variant="contained"
                    onClick={() => setPasswordDialogOpen(true)}
                  >
                    Changer le mot de passe
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Onglet Activités */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Activités récentes
          </Typography>
          <Divider sx={{ my: 2 }} />
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : recentActivities.length === 0 ? (
            <Alert severity="info">Aucune activité récente</Alert>
          ) : (
            <List>
              {recentActivities.map((activity, index) => (
                <ListItem key={index}>
                  <ListItemIcon>{activity.icon}</ListItemIcon>
                  <ListItemText
                    primary={activity.title}
                    secondary={new Date(activity.date).toLocaleString('fr-FR')}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Onglet Statistiques */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Statistiques
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Projets
                  </Typography>
                  <Typography variant="h4">{stats.projects}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Tâches
                  </Typography>
                  <Typography variant="h4">{stats.tasks}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Rapports
                  </Typography>
                  <Typography variant="h4">{stats.reports}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Messages
                  </Typography>
                  <Typography variant="h4">{stats.messages}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Dialog changement de mot de passe */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Changer le mot de passe</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nouveau mot de passe"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, newPassword: e.target.value })
              }
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowPasswords({ ...showPasswords, new: !showPasswords.new })
                      }
                    >
                      {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, confirmPassword: e.target.value })
              }
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })
                      }
                    >
                      {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={saving || !passwordData.newPassword || !passwordData.confirmPassword}
          >
            {saving ? <CircularProgress size={24} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;


